import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  Subject,
  filter,
  firstValueFrom,
  map,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../types/api.types';

/**
 * Type d'événement émis par Supabase Realtime (basé sur les opérations Postgres).
 */
export type RealtimeEventType = 'INSERT' | 'UPDATE' | 'DELETE';

/**
 * Un événement Realtime reçu du serveur.
 *
 * <p>Format simplifié du payload Phoenix de Supabase Realtime : on extrait
 * la table, le type d'opération, les colonnes nouvelles/anciennes, et un
 * timestamp local.</p>
 */
export interface RealtimeEvent<T = Record<string, unknown>> {
  /** Table source (ex: "client", "commande"). */
  table: string;
  /** Schéma source (généralement "sch_core"). */
  schema: string;
  /** Type d'opération. */
  type: RealtimeEventType;
  /** Valeurs après modification (pour INSERT/UPDATE). */
  record?: T;
  /** Valeurs avant modification (pour UPDATE/DELETE, nécessite REPLICA IDENTITY FULL). */
  oldRecord?: T;
  /** Timestamp de réception côté client. */
  receivedAt: number;
}

/** État de la connexion. */
export type RealtimeStatus = 'DISCONNECTED' | 'CONNECTING' | 'CONNECTED' | 'ERROR';

interface RealtimeAccess {
  token: string;
  wsUrl: string;
  apiKey: string;
  expiresAt: string;
  atelierId: number;
}

interface PhoenixMessage {
  topic: string;
  event: string;
  payload: any;
  ref: string | null;
  join_ref?: string | null;
}

/**
 * Client Supabase Realtime implémenté en TypeScript natif (Phoenix Channels v2).
 *
 * <p>Pourquoi pas {@code @supabase/supabase-js} ? Pour ne pas alourdir le
 * bundle frontend de la lib complète Supabase (auth, postgrest, storage…)
 * alors qu'on n'a besoin que du canal WebSocket. Le protocole Phoenix est
 * stable et tient en ~250 lignes de code.</p>
 *
 * <p>Cycle de vie :</p>
 * <ol>
 *   <li>{@link #start} — récupère un token via {@code /api/v1/realtime/token}
 *       et ouvre la connexion WebSocket.</li>
 *   <li>{@link #subscribeTable} — rejoint un topic Realtime
 *       ({@code realtime:public:client} par exemple) et retourne un Observable
 *       d'événements typés.</li>
 *   <li>Au retour réseau / déconnexion : reconnect exponentiel, ré-abonnement
 *       automatique aux topics actifs.</li>
 *   <li>{@link #stop} — ferme proprement.</li>
 * </ol>
 *
 * <p>Heartbeat Phoenix toutes les 30s (sinon le serveur ferme la socket).</p>
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private http = inject(HttpClient);

  /** WebSocket native. */
  private ws: WebSocket | null = null;

  /** Token + URL — rafraîchis périodiquement. */
  private access: RealtimeAccess | null = null;

  /** Topics auxquels on est abonné (pour ré-abonnement après reconnect). */
  private readonly topics = new Set<string>();

  /** Sujet global d'événements — tous les events arrivent ici puis sont filtrés. */
  private readonly _events$ = new Subject<RealtimeEvent>();

  /** État de connexion exposé. */
  private readonly _status$ = new BehaviorSubject<RealtimeStatus>('DISCONNECTED');
  readonly status$ = this._status$.asObservable();

  /** Référence Phoenix incrémentale pour matcher les replies. */
  private refCounter = 0;

  /** Timer de heartbeat. */
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  /** Timer de reconnect (back-off exponentiel). */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;

  /** True si {@link #stop} a été appelé explicitement (pas de reconnect auto). */
  private stopped = true;

  // ─── API publique ─────────────────────────────────────────────────────

  /**
   * Démarre la connexion Realtime. Idempotent : appel après login OK.
   *
   * @throws si l'utilisateur n'est pas connecté ou si Realtime est désactivé.
   */
  async start(): Promise<void> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return;
    this.stopped = false;
    await this.refreshAccess();
    this.connect();
  }

  /** Ferme la connexion et stoppe les reconnects. */
  stop(): void {
    this.stopped = true;
    this.topics.clear();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch { /* */ }
      this.ws = null;
    }
    this._status$.next('DISCONNECTED');
  }

  /**
   * S'abonne aux changements d'une table.
   *
   * @param table   nom court de la table (ex: "client"). On ajoute le schéma
   *                automatiquement si non précisé.
   * @returns Observable des événements typés. La désinscription côté Angular
   *          ne désinscrit PAS du serveur (d'autres abonnés peuvent écouter)
   *          — la déconnexion globale est gérée par {@link #stop}.
   */
  subscribeTable<T = Record<string, unknown>>(
    table: string,
    opts: { schema?: string } = {},
  ): Observable<RealtimeEvent<T>> {
    const schema = opts.schema || 'sch_core';
    const topic = `realtime:${schema}:${table}`;
    if (!this.topics.has(topic)) {
      this.topics.add(topic);
      this.joinTopic(topic);
    }
    return this._events$.pipe(
      filter((e) => e.table === table && (!opts.schema || e.schema === schema)),
      map((e) => e as RealtimeEvent<T>),
    );
  }

  // ─── interne ──────────────────────────────────────────────────────────

  private async refreshAccess(): Promise<void> {
    const url = `${environment.apiUrl}/realtime/token`;
    const res = await firstValueFrom(
      this.http.get<ApiResponse<RealtimeAccess>>(url),
    );
    this.access = res?.data;
    if (!this.access) throw new Error('Token Realtime introuvable');
  }

  private connect(): void {
    if (!this.access) return;
    this._status$.next('CONNECTING');

    const url = this.buildWsUrl();
    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.reconnectAttempts = 0;
      this._status$.next('CONNECTED');
      this.startHeartbeat();
      // Re-join tous les topics
      for (const t of this.topics) this.joinTopic(t);
    };

    this.ws.onmessage = (ev) => this.handleMessage(ev.data);

    this.ws.onerror = () => {
      this._status$.next('ERROR');
    };

    this.ws.onclose = () => {
      this._status$.next('DISCONNECTED');
      this.stopHeartbeat();
      if (!this.stopped) this.scheduleReconnect();
    };
  }

  /**
   * Construit l'URL WebSocket : on ajoute le token + l'apikey en query string.
   * Convertit http:// → ws:// et https:// → wss:// si nécessaire.
   */
  private buildWsUrl(): string {
    if (!this.access) throw new Error('Access non initialisé');
    let url = this.access.wsUrl;
    url = url.replace(/^http:\/\//, 'ws://').replace(/^https:\/\//, 'wss://');
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}token=${encodeURIComponent(this.access.token)}&apikey=${encodeURIComponent(this.access.apiKey)}&vsn=2.0.0`;
  }

  /** Envoie le message "phx_join" sur le topic Realtime concerné. */
  private joinTopic(topic: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    const ref = this.nextRef();
    this.send({
      topic,
      event: 'phx_join',
      ref,
      join_ref: ref,
      // Config : on demande les events sur tous les types d'opérations.
      payload: {
        config: {
          postgres_changes: [
            { event: '*', schema: '*', table: topicTable(topic) },
          ],
        },
      },
    });
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({
        topic: 'phoenix',
        event: 'heartbeat',
        payload: {},
        ref: this.nextRef(),
      });
    }, 30_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return;
    this.reconnectAttempts++;
    // Back-off exponentiel borné à 30s
    const delay = Math.min(30_000, 1000 * Math.pow(2, this.reconnectAttempts - 1));
    this.reconnectTimer = setTimeout(async () => {
      this.reconnectTimer = null;
      try {
        // Renouveler le token s'il est expiré ou si on a échoué 3 fois de suite
        if (this.reconnectAttempts >= 3) await this.refreshAccess();
      } catch { /* on tente quand même la reconnexion */ }
      this.connect();
    }, delay);
  }

  private send(msg: PhoenixMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    // Protocole Phoenix v2 : tableau [join_ref, ref, topic, event, payload]
    const wire = [
      msg.join_ref ?? null,
      msg.ref ?? null,
      msg.topic,
      msg.event,
      msg.payload ?? {},
    ];
    try {
      this.ws.send(JSON.stringify(wire));
    } catch {
      /* la socket vient peut-être de fermer — la reconnexion gère */
    }
  }

  /** Décode un message Phoenix v2 reçu et en extrait un RealtimeEvent. */
  private handleMessage(raw: string | ArrayBuffer | Blob): void {
    if (typeof raw !== 'string') return; // binaire non géré ici
    let parsed: any;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    // v2 : [join_ref, ref, topic, event, payload]
    if (!Array.isArray(parsed) || parsed.length < 5) return;
    const [, , topic, event, payload] = parsed;

    if (event === 'phx_reply' || event === 'phx_close' || event === 'phx_error') {
      return; // gestion control plane non exposée au caller
    }

    // Format Supabase Realtime : event "postgres_changes" avec payload.data
    if (event === 'postgres_changes' && payload?.data) {
      const data = payload.data;
      const ev: RealtimeEvent = {
        table: data.table,
        schema: data.schema || 'sch_core',
        type: (data.type || data.event_type) as RealtimeEventType,
        record: data.record || data.new_record || undefined,
        oldRecord: data.old_record || undefined,
        receivedAt: Date.now(),
      };
      this._events$.next(ev);
      return;
    }

    // Anciennes versions : event "INSERT" / "UPDATE" / "DELETE" direct.
    if (['INSERT', 'UPDATE', 'DELETE'].includes(event) && topic) {
      const parts = String(topic).split(':');
      const ev: RealtimeEvent = {
        table: parts[2] || 'unknown',
        schema: parts[1] || 'public',
        type: event as RealtimeEventType,
        record: payload?.record || payload?.new || undefined,
        oldRecord: payload?.old_record || payload?.old || undefined,
        receivedAt: Date.now(),
      };
      this._events$.next(ev);
    }
  }

  private nextRef(): string {
    this.refCounter++;
    return String(this.refCounter);
  }
}

/** Extrait le nom de table d'un topic "realtime:<schema>:<table>". */
function topicTable(topic: string): string {
  const parts = topic.split(':');
  return parts[2] || '*';
}
